import { Chip, Grid, Typography } from '@material-ui/core'
import { amber, green, grey } from '@material-ui/core/colors'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import { LotesDetail } from '../types'
import {
  camposDocumentoPCFaltando,
  tabelaComErroItinerario,
  temPC
} from '../utils/acoesTabelaFrete'

type EstadoChip = 'pendente' | 'ok' | 'erro'

interface StatusChipProps {
  label: string
  estado: EstadoChip
}

const ICONE_POR_ESTADO: Record<EstadoChip, JSX.Element> = {
  ok: <CheckCircleIcon style={{ color: green[600] }} />,
  erro: <ErrorOutlineIcon style={{ color: amber[800] }} />,
  pendente: <RadioButtonUncheckedIcon style={{ color: grey[500] }} />
}

function StatusChip({ label, estado }: StatusChipProps) {
  return (
    <Grid item>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        icon={ICONE_POR_ESTADO[estado]}
      />
    </Grid>
  )
}

interface Props {
  lote: Pick<
    LotesDetail,
    | 'codigoTabelaFrete'
    | 'filialPC'
    | 'numeroPC'
    | 'vinculoTabelaClienteIntegrado'
    | 'Pernas'
    | 'pagadorTomador'
    | 'remetente'
    | 'localColeta'
    | 'destinatario'
  >
}

// Status persistente das integrações do lote: o `ConditionalTooltip` dos botões só explica o motivo
// de um botão desabilitado ao passar o mouse — aqui o estado fica visível o tempo todo.
export function StatusIntegracaoAcoes({ lote }: Props) {
  const hasTabela = !!lote.codigoTabelaFrete
  // Ver GerarTabelaFreteService: o CODFRE é persistido mesmo quando o itinerário é rejeitado.
  const tabelaComErro = tabelaComErroItinerario(lote)
  const hasPC = temPC(lote.numeroPC)
  const vinculado = !!lote.vinculoTabelaClienteIntegrado
  const camposFaltandoPC = camposDocumentoPCFaltando(lote)
  const pcBloqueadaPorCampos =
    hasTabela && !hasPC && camposFaltandoPC.length > 0

  const estadoTabela: EstadoChip = !hasTabela
    ? 'pendente'
    : tabelaComErro
    ? 'erro'
    : 'ok'

  return (
    <Grid container direction="column" spacing={1} style={{ marginTop: 8 }}>
      <Grid item>
        <Grid container spacing={1}>
          <StatusChip
            estado={estadoTabela}
            label={
              !hasTabela
                ? 'Tabela de Frete ainda não criada'
                : tabelaComErro
                ? `Tabela de Frete criada com erro no itinerário (código ${lote.codigoTabelaFrete})`
                : `Tabela de Frete criada (código ${lote.codigoTabelaFrete})`
            }
          />
          <StatusChip
            estado={hasPC ? 'ok' : pcBloqueadaPorCampos ? 'erro' : 'pendente'}
            label={
              hasPC
                ? `PC criada (${lote.filialPC}-${lote.numeroPC})`
                : pcBloqueadaPorCampos
                ? 'PC bloqueada: faltam campos no lote'
                : 'PC ainda não criada'
            }
          />
          <StatusChip
            estado={vinculado ? 'ok' : 'pendente'}
            label={
              vinculado
                ? 'Tabela por Cliente vinculada'
                : 'Tabela por Cliente ainda não vinculada'
            }
          />
        </Grid>
      </Grid>

      {tabelaComErro && (
        <Grid item>
          <Typography variant="caption" style={{ color: amber[800] }}>
            A Rodopar gravou o cabeçalho da tabela (código{' '}
            {lote.codigoTabelaFrete}), mas rejeitou o itinerário — geralmente
            por um item de tarifa não cadastrado. Enquanto isso não for
            corrigido, "Reajuste de Valores" não funciona (precisa do itinerário
            gravado). Confira o motivo no "Histórico de integração", peça o
            cadastro do item de tarifa na Rodopar e clique em "Criar Tabela de
            Frete" novamente para tentar gravar o itinerário — o mesmo código é
            reaproveitado, não cria uma tabela nova.
          </Typography>
        </Grid>
      )}

      {pcBloqueadaPorCampos && (
        <Grid item>
          <Typography variant="caption" style={{ color: amber[800] }}>
            Preencha no cadastro do lote (seção "Informações Adicionais") antes
            de criar a PC: {camposFaltandoPC.join(', ')}. A Rodopar valida esses
            campos contra o cadastro dela e recusa a PC sem eles.
          </Typography>
        </Grid>
      )}

      {hasTabela && !tabelaComErro && (
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            A tabela de frete já foi criada (código {lote.codigoTabelaFrete}).
            Para alterar valores, use o "Reajuste de Valores".
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
